import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";

// Assets
import saveIcon from "../assets/save_icon.png";
import viewIcon from "../assets/view.png";
import deleteIcon from "../assets/delete_Icon.png";

// Utils
import { DataContext } from "../utils/DataContext";
import { UserAuth } from "../utils/AuthContext";
import { shouldCloseModal } from "../utils/shouldCloseModal";

// Components
import Modal from "./Modal";
import Dropdown from "./Dropdown";
import UploadButton from "./UploadButton";
import CustomButton from "./CustomButton";
import AreYouSureModal from "./AreYouSureModal";
import ConfirmationBox from "./ConfirmationBox";
import Loader from "./Loader";

export default function UploadInvoiceHeaderAndFooterModal({
  setShowInvoiceHeaderModal,
  withSubCompany = true,
}) {
  const { userData } = useContext(UserAuth);

  const {
    subCompanies,
    getSubCompanies,
    selectedSubCompany,
    handleSelectSubCompany,
  } = useContext(DataContext);

  const navigate = useNavigate();

  const { t } = useTranslation("general");

  const [areYouSureModal, setAreYouSureModal] = useState(false);
  const [invoiceHeaderImage, setInvoiceHeaderImage] = useState(null);
  const [invoiceFooterImage, setInvoiceFooterImage] = useState(null);
  const [filesStatus, setFilesStatus] = useState({
    invoiceHeader: false,
    invoiceFooter: false,
  });
  const [loadingAcceptFile, setLoadingAcceptFile] = useState(false);

  const handleFileUpload = async (state, image_type) => {
    if (!state) return;
    try {
      setLoadingAcceptFile(true);
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/uploadInvoiceImages`,
        state,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response?.status === 200) {
        if (image_type === "invoiceHeader") {
          setFilesStatus((prev) => ({
            ...prev,
            invoiceHeader: true,
          }));
        } else if (image_type === "invoiceFooter") {
          setFilesStatus((prev) => ({
            ...prev,
            invoiceFooter: true,
          }));
        }
      }
    } catch (error) {
      console.error("Upload failed", error);
    } finally {
      setLoadingAcceptFile(false);
    }
  };

  const handleAttemptCloseModal = () => {
    setAreYouSureModal(true);
  };

  const handleCloseModal = () => {
    setInvoiceHeaderImage(null);
    setInvoiceFooterImage(null);
    setShowInvoiceHeaderModal(false);
  };

  const handlePreviewInvoiceTemplate = () => {
    const headerImageData = invoiceHeaderImage?.preview
      ? {
          preview: {
            url: invoiceHeaderImage.preview.url,
            name: invoiceHeaderImage.preview.name,
            size: invoiceHeaderImage.preview.size,
            status: filesStatus.invoiceHeader,
          },
        }
      : null;

    const footerImageData = invoiceFooterImage?.preview
      ? {
          preview: {
            url: invoiceFooterImage.preview.url,
            name: invoiceFooterImage.preview.name,
            size: invoiceFooterImage.preview.size,
            status: filesStatus.invoiceFooter,
          },
        }
      : null;

    navigate(`/previewItemInvoiceTemplate`, {
      state: {
        data: {
          // For Upload
          headerFile: invoiceHeaderImage?.formData.get("images"),
          // For Preview
          invoiceHeaderImage: headerImageData,
          // For Upload
          footerFile: invoiceFooterImage?.formData.get("images"),
          // For Preview
          invoiceFooterImage: footerImageData,
        },
      },
    });
  };

  return (
    <>
      <Modal
        title="uploadDesign"
        onClose={handleAttemptCloseModal}
        // withDropdown
        footer={
          <div className="o-row o-row--horizontal-align_center ">
            <CustomButton
              title={t("view")}
              onClick={handlePreviewInvoiceTemplate}
              icon={viewIcon}
              iconSize={20}
              variant="blank"
              classList="u-width_full-width"
            />
          </div>
        }
      >
        {withSubCompany && (
          <div className="o-column o-column--gap-size_300 u-padding--bottom_500">
            <label htmlFor="amountInput" className="u-min-width-specific_100">
              {t("chooseTheCompany")}:
            </label>
            <div>
              <Dropdown
                options={subCompanies}
                selected={selectedSubCompany}
                setSelected={(subCompany) =>
                  handleSelectSubCompany(subCompany, "noRequest")
                }
                thick
                nameInAr={true}
                getData={getSubCompanies}
              />
            </div>
          </div>
        )}

        <div className="o-column o-column--gap-size_300">
          <label className="u-min-width-specific_150">
            {t("invoiceHeader")}:
          </label>
          <div className="o-row o-row--gap-size_300">
            <UploadButton
              title="uploadInvoiceHeader"
              invoiceId={`${userData?.organizationId}---${selectedSubCompany?.id}`}
              invoiceType="invoiceHeader"
              imageType={`organization ${userData?.organizationId} invoice header`}
              allowedTypes="image/*"
              classList="o-row o-row--horizontal-align_center"
              image={invoiceHeaderImage}
              setImage={setInvoiceHeaderImage}
            />
          </div>

          {/* Preview area */}
          {invoiceHeaderImage?.preview?.url ? (
            <div className="u-width_full-width u-margin--top_300 u-margin--bottom_300">
              <div className="o-row o-row--vertical-align_center o-row--gap-size_500">
                <img
                  src={invoiceHeaderImage.preview.url}
                  alt="Invoice Header Preview"
                  className="u-width-specific_300"
                />
              </div>
              <p className="u-margin--top_200 t-size_small">
                {invoiceHeaderImage.preview.name} (
                {Math.round(invoiceHeaderImage.preview.size / 1024)} KB)
              </p>
              {filesStatus?.invoiceHeader ? (
                <ConfirmationBox>{t("invoiceHeaderAccepted")}</ConfirmationBox>
              ) : (
                <div className="o-row o-row--gap-size_500">
                  <CustomButton
                    title={t("delete")}
                    variant="red"
                    icon={deleteIcon}
                    iconSize={16}
                    onClick={() => setInvoiceHeaderImage(null)}
                    disabled={loadingAcceptFile}
                  />
                  <CustomButton
                    title={t("accept")}
                    onClick={() =>
                      handleFileUpload(
                        invoiceHeaderImage?.formData,
                        "invoiceHeader"
                      )
                    }
                    icon={saveIcon}
                    iconSize={16}
                    disabled={loadingAcceptFile}
                  />
                </div>
              )}
              {loadingAcceptFile && <Loader fullHeight />}
            </div>
          ) : null}
        </div>

        <div className="o-column o-column--gap-size_300">
          <label className="u-min-width-specific_150">
            {t("invoiceFooter")}:
          </label>
          <div className="o-row o-row--gap-size_300">
            <UploadButton
              title="uploadInvoiceFooter"
              invoiceId={`${userData?.organizationId}---${selectedSubCompany?.id}`}
              invoiceType="invoiceFooter"
              imageType={`organization ${userData?.organizationId} invoice footer`}
              allowedTypes="image/*"
              classList="o-row o-row--horizontal-align_center"
              image={invoiceFooterImage}
              setImage={setInvoiceFooterImage}
            />
          </div>

          {/* Preview area */}
          {invoiceFooterImage?.preview?.url ? (
            <div className="u-width_full-width u-margin--top_300 u-margin--bottom_300">
              <div className="o-row o-row--vertical-align_center o-row--gap-size_500">
                <img
                  src={invoiceFooterImage.preview.url}
                  alt="Invoice Footer Preview"
                  className="u-width-specific_300"
                />
              </div>
              <p className="u-margin--top_200 t-size_small">
                {invoiceFooterImage.preview.name} (
                {Math.round(invoiceFooterImage.preview.size / 1024)} KB)
              </p>

              {filesStatus?.invoiceFooter ? (
                <ConfirmationBox>{t("invoiceFooterAccepted")}</ConfirmationBox>
              ) : (
                <div className="o-row o-row--gap-size_500">
                  <CustomButton
                    title={t("delete")}
                    variant="red"
                    icon={deleteIcon}
                    iconSize={16}
                    onClick={() => setInvoiceFooterImage(null)}
                    disabled={loadingAcceptFile}
                  />
                  <CustomButton
                    title={t("accept")}
                    onClick={() =>
                      handleFileUpload(
                        invoiceFooterImage?.formData,
                        "invoiceFooter"
                      )
                    }
                    icon={saveIcon}
                    iconSize={16}
                    disabled={loadingAcceptFile}
                  />
                </div>
              )}

              {loadingAcceptFile && <Loader />}
            </div>
          ) : null}
        </div>
      </Modal>

      {areYouSureModal ? (
        <AreYouSureModal
          shouldCloseModal={shouldCloseModal({
            invoiceHeaderImage,
            invoiceFooterImage,
          })}
          onClose={() => setAreYouSureModal(false)}
          onConfirm={handleCloseModal}
          confirmIconType="whiteCheck"
        />
      ) : null}
    </>
  );
}
